import { useEffect, useState } from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import { DateTime } from "luxon";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { getAuthenticatedServerClient } from "../../api/server.api";
import { DialogCloseButton } from "../../misc/DialogCloseButton";
import type { DialogProps } from "../../misc/DialogProps";
import { autoFormsProvider, type DrivingSchoolAutoForm } from "../../providers/autoFormsProvider";
import { type BKFCourse } from "../../providers/coursesProvider";
import { courseParticipantsProvider } from "../../providers/courseParticipantsProvider";
import { studentsProvider, type Student } from "../../providers/studentsProvider";
import {
  createCourseAutoFormsDownload,
  downloadCourseAutoForms,
  generateCourseAutoForms,
  getCourseMonths,
  prepareCourseFormTasks,
  SGB_III_FORM_NAME,
  type CourseFormTask,
  type FailedCourseAutoForm,
  type GeneratedCourseAutoForm,
} from "./courseAutoFormDownload";

export function CreateCourseAutoFormsDialog(
  props: {
    course: BKFCourse;
  } & DialogProps,
) {
  const { course, open, onClose } = props;
  const [participants, setParticipants] = useState<Array<Student>>([]);
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<Array<string>>([]);
  const [forms, setForms] = useState<Array<DrivingSchoolAutoForm>>([]);
  const [selectedFormIds, setSelectedFormIds] = useState<Array<string>>([]);
  const [includeSGBIII, setIncludeSGBIII] = useState(false);
  const [sgbIIIMonth, setSGBIIIMonth] = useState<DateTime | null>(null);
  const hasValidCoursePeriod = course.start.isValid && course.end.isValid && course.start <= course.end;
  const courseMonths = includeSGBIII && hasValidCoursePeriod ? getCourseMonths(course.start, course.end) : [];
  const selectedCourseMonth = courseMonths.find(
    (month) => month.toFormat("yyyy-MM") === sgbIIIMonth?.toFormat("yyyy-MM"),
  );
  const [loadingForms, setLoadingForms] = useState(true);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [completed, setCompleted] = useState(0);
  const [total, setTotal] = useState(0);
  const [generated, setGenerated] = useState<Array<GeneratedCourseAutoForm>>([]);
  const [failed, setFailed] = useState<Array<FailedCourseAutoForm>>([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingForms(true);
    setLoadError(false);
    setSelectedParticipantIds([]);
    setSelectedFormIds([]);
    setIncludeSGBIII(false);
    setSGBIIIMonth(null);
    setGenerated([]);
    setFailed([]);
    void runSafely(
      async () => {
        const [allForms, courseParticipants] = await Promise.all([
          autoFormsProvider.getAllForDrivingSchool(course.drivingSchoolId),
          courseParticipantsProvider.getAllForCourse(course.id),
        ]);
        const { data: students } = courseParticipants.length
          ? await studentsProvider.getMany("students", {
              ids: courseParticipants.map((participant) => participant.studentId),
            })
          : { data: [] };
        if (!cancelled) {
          setParticipants(students.sort((a, b) => a.name.localeCompare(b.name, "de")));
          setForms(
            allForms
              .filter((form) => !form.deleted && form.pdfTemplate && form.pdfTemplateBaseFile)
              .sort((a, b) => a.name.localeCompare(b.name, "de")),
          );
          setLoadingForms(false);
        }
      },
      {
        successMessage: "",
        errorMessage: "Teilnehmer oder Formulare konnten nicht geladen werden.",
        onError: () => {
          if (!cancelled) {
            setLoadingForms(false);
            setLoadError(true);
          }
        },
      },
    );
    return () => {
      cancelled = true;
    };
  }, [open, course.id, course.drivingSchoolId, loadAttempt]);

  const download = async (documents: Array<GeneratedCourseAutoForm>) => {
    const { blob, fileName } = await createCourseAutoFormsDownload(documents, course.name);
    downloadCourseAutoForms(blob, fileName);
  };

  const generate = async (retry = false) => {
    setGenerating(true);
    await runSafely(
      async () => {
        let tasks: Array<CourseFormTask>;
        if (retry) {
          tasks = failed.map(({ task }) => task);
        } else {
          const participants = await courseParticipantsProvider.getAllForCourse(course.id);
          const { data: students } = await studentsProvider.getMany("students", { ids: selectedParticipantIds });
          tasks = prepareCourseFormTasks({
            selectedParticipantIds,
            students,
            participants,
            forms: forms.filter((form) => selectedFormIds.includes(form.id)),
            sgbIIIMonth: includeSGBIII ? (selectedCourseMonth ?? null) : undefined,
          });
        }
        setTotal(tasks.length);
        setCompleted(0);
        const client = await getAuthenticatedServerClient();
        const result = await generateCourseAutoForms(
          tasks,
          async (task) => {
            if (task.kind === "sgbIII") {
              const pdf = (await client.createSGBIIIBescheinigungFehlzeiten({
                params: { studentId: task.studentId, year: String(task.year), month: String(task.month) },
                responseType: "arraybuffer",
              })) as unknown as ArrayBuffer;
              return new Blob([pdf], { type: "application/pdf" });
            }
            return client.fillOutForm({
              params: { id: task.studentId, formId: task.formId },
              queries: { withoutBackground: false, quoteId: task.bookedQuoteId },
              responseType: "blob",
            }) as unknown as Promise<Blob>;
          },
          setCompleted,
        );
        const allGenerated = [...generated, ...result.generated];
        setGenerated(allGenerated);
        setFailed(result.failed);
        if (result.failed.length > 0) {
          for (const { task, error } of result.failed) {
            console.error(
              `Formular ${task.formName} für Teilnehmer ${task.studentId} konnte nicht erstellt werden`,
              error,
            );
          }
          showErrorToast(
            `${result.failed.length} von ${tasks.length} PDFs konnten nicht erstellt werden. Fehlgeschlagene PDFs können erneut versucht werden.`,
          );
        } else {
          await download(allGenerated);
          showSuccessToast(`${allGenerated.length} PDF${allGenerated.length === 1 ? "" : "s"} heruntergeladen.`);
          onClose();
        }
      },
      { errorMessage: "Fehler beim Erstellen oder Herunterladen der Formulare.", successMessage: "" },
    );
    setGenerating(false);
  };

  const hasResults = generated.length > 0 || failed.length > 0;
  const close = () => {
    if (!generating) onClose();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Formulare für Kursteilnehmer erstellen</DialogTitle>
      <DialogCloseButton onClick={close} disabled={generating} />
      <DialogContent
        sx={{
          "& .MuiTypography-root, & .MuiAlert-message": { fontSize: "14px" },
          "& .MuiFormControlLabel-root .MuiCheckbox-root": { p: 0.75 },
        }}
      >
        <Alert severity="info" sx={{ mb: 2 }}>
          Die PDFs werden nur heruntergeladen und nicht in den Schülerdokumenten gespeichert.
        </Alert>
        {loadingForms && <Typography>Teilnehmer und Formulare werden geladen …</Typography>}
        {loadError && <Button onClick={() => setLoadAttempt((attempt) => attempt + 1)}>Laden erneut versuchen</Button>}
        {!loadingForms && !loadError && (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Teilnehmer auswählen ({selectedParticipantIds.length} ausgewählt)
            </Typography>
            {participants.length === 0 ? (
              <Typography>Keine Teilnehmer im Kurs vorhanden.</Typography>
            ) : (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedParticipantIds.length === participants.length}
                      indeterminate={
                        selectedParticipantIds.length > 0 && selectedParticipantIds.length < participants.length
                      }
                      disabled={generating || hasResults}
                      onChange={(event) =>
                        setSelectedParticipantIds(event.target.checked ? participants.map((student) => student.id) : [])
                      }
                    />
                  }
                  label="Alle Teilnehmer auswählen"
                  sx={{ display: "block" }}
                />
                <div style={{ maxHeight: 240, overflowY: "auto", paddingLeft: 11 }}>
                  {participants.map((student) => (
                    <FormControlLabel
                      key={student.id}
                      control={
                        <Checkbox
                          checked={selectedParticipantIds.includes(student.id)}
                          disabled={generating || hasResults}
                          onChange={(event) =>
                            setSelectedParticipantIds((ids) =>
                              event.target.checked ? [...ids, student.id] : ids.filter((id) => id !== student.id),
                            )
                          }
                        />
                      }
                      label={student.name}
                      sx={{ display: "block" }}
                    />
                  ))}
                </div>
              </>
            )}
            <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 600 }}>
              Formularvorlage auswählen
            </Typography>
          </>
        )}
        {!loadingForms && !loadError && forms.length === 0 && (
          <Typography>Keine eingerichteten Auto-Formulare vorhanden.</Typography>
        )}
        {!loadingForms &&
          !loadError &&
          forms.map((form) => (
            <FormControlLabel
              key={form.id}
              control={
                <Checkbox
                  checked={selectedFormIds.includes(form.id)}
                  disabled={generating || hasResults}
                  onChange={(event) =>
                    setSelectedFormIds((ids) =>
                      event.target.checked ? [...ids, form.id] : ids.filter((id) => id !== form.id),
                    )
                  }
                />
              }
              label={form.name}
              sx={{ display: "block" }}
            />
          ))}
        {!loadingForms && !loadError && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeSGBIII}
                  disabled={generating || hasResults}
                  onChange={(event) => {
                    setIncludeSGBIII(event.target.checked);
                    setSGBIIIMonth(null);
                  }}
                />
              }
              label={SGB_III_FORM_NAME}
            />
            {includeSGBIII && !hasValidCoursePeriod && (
              <Alert severity="error" sx={{ mt: 1 }}>
                Der Kurszeitraum ist ungültig. Bitte korrigiere Kursbeginn und Kursende.
              </Alert>
            )}
            {includeSGBIII && (
              <TextField
                select
                label="Monat und Jahr"
                value={selectedCourseMonth?.toFormat("yyyy-MM") ?? ""}
                onChange={(event) => {
                  const month = courseMonths.find((month) => month.toFormat("yyyy-MM") === event.target.value);
                  if (!month) throw new Error("Der ausgewählte Monat liegt nicht im Kurszeitraum.");
                  setSGBIIIMonth(month);
                }}
                disabled={generating || hasResults || !hasValidCoursePeriod}
                required
                size="small"
                fullWidth
                sx={{ mt: 1 }}
              >
                <MenuItem value="" disabled>
                  Bitte Monat auswählen
                </MenuItem>
                {courseMonths.map((month) => (
                  <MenuItem key={month.toFormat("yyyy-MM")} value={month.toFormat("yyyy-MM")}>
                    {month.setLocale("de").toFormat("LLLL yyyy")}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </>
        )}
        {generating && (
          <Typography>
            PDFs erstellt: {completed} von {total}
          </Typography>
        )}
        {failed.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {failed.length} PDFs fehlgeschlagen:
            <ul>
              {failed.map(({ task, error }) => (
                <li key={`${task.studentId}-${task.kind === "autoForm" ? task.formId : "sgbIII"}`}>
                  {task.studentName} – {task.formName}: {error instanceof Error ? error.message : String(error)}
                </li>
              ))}
            </ul>
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap" }}>
        {(selectedFormIds.length > 0 || includeSGBIII) && !hasResults && (
          <Typography sx={{ mr: "auto", fontSize: "14px" }}>
            {selectedParticipantIds.length * (selectedFormIds.length + Number(includeSGBIII)) === 1
              ? "1 PDF wird erstellt."
              : `${selectedParticipantIds.length * (selectedFormIds.length + Number(includeSGBIII))} PDFs werden erstellt.`}
          </Typography>
        )}
        {generated.length > 0 && failed.length > 0 && (
          <Button
            disabled={generating}
            onClick={() =>
              void runSafely(() => download(generated), {
                errorMessage: "Fehler beim Herunterladen der erfolgreichen PDFs.",
                successMessage: "",
              })
            }
          >
            Erfolgreiche herunterladen
          </Button>
        )}
        {failed.length > 0 ? (
          <LoadingButton loading={generating} onClick={() => void generate(true)} variant="contained">
            Fehlgeschlagene erneut versuchen
          </LoadingButton>
        ) : (
          <LoadingButton
            loading={generating}
            disabled={
              loadingForms ||
              loadError ||
              selectedParticipantIds.length === 0 ||
              (selectedFormIds.length === 0 && !includeSGBIII) ||
              (includeSGBIII &&
                (!selectedCourseMonth || selectedCourseMonth.year < 2000 || selectedCourseMonth.year > 2100)) ||
              hasResults
            }
            onClick={() => void generate()}
            variant="contained"
          >
            PDFs herunterladen
          </LoadingButton>
        )}
        {generated.length > 0 && failed.length === 0 && !generating && (
          <Button
            onClick={() =>
              void runSafely(() => download(generated), {
                errorMessage: "Fehler beim Herunterladen.",
                successMessage: "",
              })
            }
          >
            Download erneut versuchen
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
